import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, SearchIcon } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { pcmpLookupDepartmentsQueryOptions } from '../../api/pcmpLookups.api';
import { pcmpProjectPickerQueryOptions } from '../../api/pcmpProjects.api';
import { pcmpStatusCatalogQueryOptions } from '../../api/pcmpStatus.api';
import {
  PCMP_DIVISION_LABELS,
  PCMP_QUEUE_FILTER_LIMITS,
  PCMP_REASON_STATUS,
} from '../../constants/pcmpQueue.constants';
import {
  hasPcmpReasonFilter,
  type PcmpQueueFilterController,
} from '../../hooks/usePcmpQueueFilters';
import type {
  PcmpDisbursementConfirmStatus,
  PcmpDisbursementVerifyStatus,
} from '../../types/pcmpProposal.types';
import { PCMP_DIVISIONS, type PcmpDivision } from '../../types/pcmpQueue.types';
import { PcmpCodeSelect } from './PcmpCodeSelect';

interface PcmpQueueFilterBarProps {
  controller: PcmpQueueFilterController;
  /** Keeps element ids unique per queue, e.g. `pcmp-mkt`. */
  idPrefix: string;
  /** Offer the MO confirmation and MIS verification filters of a payout (LB-1195). */
  showPayoutFilters?: boolean;
}

const DIVISION_OPTIONS = PCMP_DIVISIONS.map((code) => ({
  code,
  label: PCMP_DIVISION_LABELS[code],
}));

export function PcmpQueueFilterBar({
  controller,
  idPrefix,
  showPayoutFilters = false,
}: PcmpQueueFilterBarProps) {
  const {
    filters,
    activeFilterCount,
    isDateRangeInvalid,
    isBatchNoInvalid,
    setDraft,
    handleApply,
    handleReset,
  } = controller;

  const { data: projects, isLoading: projectsLoading } = useQuery(pcmpProjectPickerQueryOptions());
  const { data: departments, isLoading: departmentsLoading } = useQuery(
    pcmpLookupDepartmentsQueryOptions(),
  );
  const { data: catalog, isLoading: catalogLoading } = useQuery(pcmpStatusCatalogQueryOptions());

  const reasonOptions = useMemo(() => {
    if (!catalog || !hasPcmpReasonFilter(filters.status)) return undefined;
    return catalog[PCMP_REASON_STATUS[filters.status as keyof typeof PCMP_REASON_STATUS]];
  }, [catalog, filters.status]);

  // A department belongs to one PCMP division, so a division narrows the departments.
  // One that came from the URL but is not in the list (not a region, or of another
  // division) stays visible, so the filter it applies can be seen and cleared.
  const departmentOptions = useMemo(() => {
    if (!departments) return undefined;
    const options = departments
      .filter((department) => !filters.division || department.divisionCode === filters.division)
      .map(({ id, name }) => ({ id, name }));
    const { departmentId } = filters;
    if (departmentId !== undefined && !options.some((option) => option.id === departmentId)) {
      const known = departments.find((department) => department.id === departmentId);
      options.unshift({ id: departmentId, name: known?.name ?? `Department #${departmentId}` });
    }
    return options;
  }, [departments, filters]);

  const handleDivisionChange = (division: PcmpDivision | undefined) => {
    setDraft('division', division);
    const department = departments?.find((item) => item.id === filters.departmentId);
    if (division && department && department.divisionCode !== division) {
      setDraft('departmentId', undefined);
    }
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        handleApply();
      }}
      className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-project`}>Project</Label>
          <OptionSelect
            id={`${idPrefix}-project`}
            options={projects}
            isLoading={projectsLoading}
            value={filters.projectId}
            onChange={(value) => setDraft('projectId', value)}
            placeholder="All"
            className="h-10"
            clearable
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-ref`}>Ref No</Label>
          <Input
            id={`${idPrefix}-ref`}
            value={filters.refNo}
            onChange={(event) => setDraft('refNo', event.target.value)}
            maxLength={PCMP_QUEUE_FILTER_LIMITS.refNo}
            placeholder="Part of the ref no"
            className="h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-teacher`}>Teacher</Label>
          <Input
            id={`${idPrefix}-teacher`}
            value={filters.teacher}
            onChange={(event) => setDraft('teacher', event.target.value)}
            maxLength={PCMP_QUEUE_FILTER_LIMITS.teacher}
            placeholder="Name or mobile"
            className="h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-from`}>Proposed From</Label>
          <DatePickerInput
            id={`${idPrefix}-from`}
            value={filters.dateFrom}
            onChange={(value) => setDraft('dateFrom', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-to`}>Proposed To</Label>
          <DatePickerInput
            id={`${idPrefix}-to`}
            value={filters.dateTo}
            onChange={(value) => setDraft('dateTo', value)}
            aria-invalid={isDateRangeInvalid}
            className="sm:h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-status`}>Status</Label>
          <PcmpCodeSelect
            id={`${idPrefix}-status`}
            options={catalog?.resolvedStatuses}
            value={filters.status}
            onChange={(value) => setDraft('status', value)}
            disabled={catalogLoading}
          />
        </div>
        {reasonOptions ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-reason`}>Reason</Label>
            <PcmpCodeSelect
              id={`${idPrefix}-reason`}
              options={reasonOptions}
              value={filters.reason}
              onChange={(value) => setDraft('reason', value)}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-division`}>Division</Label>
          <PcmpCodeSelect<PcmpDivision>
            id={`${idPrefix}-division`}
            options={DIVISION_OPTIONS}
            value={filters.division}
            onChange={handleDivisionChange}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-department`}>Department</Label>
          <OptionSelect
            id={`${idPrefix}-department`}
            options={departmentOptions}
            isLoading={departmentsLoading}
            value={filters.departmentId}
            onChange={(value) => setDraft('departmentId', value)}
            placeholder="All"
            className="h-10"
            clearable
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-batch`}>Batch No</Label>
          <Input
            id={`${idPrefix}-batch`}
            inputMode="numeric"
            value={filters.batchNo}
            onChange={(event) => setDraft('batchNo', event.target.value)}
            aria-invalid={isBatchNoInvalid}
            placeholder="Route or urgent batch"
            className="h-10"
          />
        </div>
        {showPayoutFilters ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-acc-disb-confirm`}>Acc Disb Confirm</Label>
              <PcmpCodeSelect<PcmpDisbursementConfirmStatus>
                id={`${idPrefix}-acc-disb-confirm`}
                options={catalog?.disbursementConfirmStatuses}
                value={filters.accDisbConfirm}
                onChange={(value) => setDraft('accDisbConfirm', value)}
                disabled={catalogLoading}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-disb-verify`}>Disb Verify</Label>
              <PcmpCodeSelect<PcmpDisbursementVerifyStatus>
                id={`${idPrefix}-disb-verify`}
                options={catalog?.disbursementVerifyStatuses}
                value={filters.disbVerify}
                onChange={(value) => setDraft('disbVerify', value)}
                disabled={catalogLoading}
              />
            </div>
          </>
        ) : null}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-destructive" role="alert">
          {isDateRangeInvalid ? 'Proposed To must be on or after Proposed From. ' : null}
          {isBatchNoInvalid ? 'Batch No must be a whole number.' : null}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            type="submit"
            disabled={isDateRangeInvalid || isBatchNoInvalid}
            className="h-10 gap-1.5 shadow-sm"
          >
            <SearchIcon className="size-4" />
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={activeFilterCount === 0}
            className="h-10 gap-1.5 shadow-sm"
          >
            <RotateCcw className="size-4" />
            Reset{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        </div>
      </div>
    </form>
  );
}
